import { luminance } from "./utils";

type Branding = {
  logo: string | null;
  colour: string;
  teamName: string;
};

type Props = {
  documentName: string;
  documentUrl: string;
  branding?: Branding | null;
};

const MainSideCard = (props: Props) => {
  const { documentName, documentUrl, branding } = props;

  const hasBranding = branding?.logo && branding?.teamName;

  const handleDownload = async () => {
    try {
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = documentName || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab if fetch fails (e.g. CORS)
      window.open(documentUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      {/* Mobile Top Banner */}
      <div className="fixed top-0 left-0 w-full h-16 bg-white border-b border-gray-200 z-20 px-4 py-2 flex items-center xl:hidden">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {hasBranding && (
            <img src={branding.logo!} alt="" className="h-10 w-10 flex-shrink-0 object-contain" />
          )}
          <div className="flex flex-col flex-1 min-w-0 justify-center">
            {hasBranding && (
              <div className="font-title font-semibold text-xs truncate" style={{ color: branding?.colour ?? '#6b7280' }}>{branding.teamName}</div>
            )}
            <div className="font-title font-semibold text-base text-black truncate">
              {documentName}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Side Card */}
      <div className="hidden xl:flex xl:static xl:flex-col xl:max-w-[320px] 2xl:max-w-[360px] 3xl:max-w-[440px] 2k:max-w-[480px] 4k:max-w-[520px] z-10 h-min">
        <div className="bg-white border border-gray-200 rounded-[8px] flex flex-col overflow-hidden">
          {/* Header with branding */}
          {hasBranding && (
            <div className="w-full px-6 py-4 3xl:px-8 3xl:py-6 2k:px-10 2k:py-7 4k:px-12 4k:py-8 border-b border-gray-200">
              <div className="flex items-center gap-3 xl:gap-2 2xl:gap-3 3xl:gap-4 2k:gap-5 4k:gap-6">
                <img src={branding.logo!} alt="" className="h-10 w-10 flex-shrink-0 object-contain" />
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <div className="text-sm xl:text-xs 2xl:text-sm 3xl:text-base 2k:text-lg 4k:text-xl text-gray-500 truncate">Document Preview</div>
                  <div className="text-xs xl:text-sm 2xl:text-xs 3xl:text-xs 2k:text-[13px] 4k:text-sm font-title font-semibold text-black truncate">{branding.teamName}</div>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex flex-col gap-6 py-8 px-6 3xl:py-10 3xl:px-8 2k:py-12 2k:px-10 4k:py-14 4k:px-12">
            {/* Title */}
            <div className="font-title font-semibold text-lg xl:text-base 2xl:text-lg 3xl:text-2xl 2k:text-3xl 4k:text-3xl text-black leading-5 break-words">
              {documentName}
            </div>

            {/* Download Button */}
            <button
              className="btn btn-primary rounded-[8px] h-[40px] xl:h-[36px] 2xl:h-[40px] 3xl:h-[48px] 2k:h-[52px] 4k:h-[56px] text-sm xl:text-xs 2xl:text-sm 3xl:text-base 2k:text-lg 4k:text-xl font-title font-semibold w-full"
              onClick={handleDownload}
              style={{
                backgroundColor: branding?.colour ?? '#1875fb',
                color: luminance(branding?.colour ?? '#1875fb') >= 0.5 ? 'black' : 'white'
              }}
            >
              Download Document
            </button>
          </div>

          {/* Footer */}
          <div className="text-center border-t border-gray-200 p-1">
            <a
              href="https://www.portant.co/terms-and-conditions"
              target="_blank"
              rel="noreferrer"
              className="font-title font-semibold text-sm xl:text-xs 2xl:text-sm 3xl:text-xs 2k:text-[13px] 4k:text-sm text-[#76777c] text-center cursor-pointer hover:text-[#5d5e63]"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Banner */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 z-20 xl:hidden">
        <div className="px-4 py-4 flex items-center justify-center">
          <button
            className="btn btn-primary px-8 rounded-[8px] h-[40px] text-sm font-title font-semibold w-full max-w-md"
            onClick={handleDownload}
            style={{
              backgroundColor: branding?.colour ?? '#1875fb',
              color: luminance(branding?.colour ?? '#1875fb') >= 0.5 ? 'black' : 'white'
            }}
          >
            Download Document
          </button>
        </div>
      </div>
    </>
  );
};

export default MainSideCard;
